import { useParams, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowLeft, CheckCircle2, Target, Zap } from 'lucide-react'
import { trackEvent } from '../analytics'
import { servicePagesContent, serviceFAQs } from '../data/servicePages'

const icons = [Target, Zap, CheckCircle2]

export default function ServicePage() {
  const { slug } = useParams<{ slug: string }>()
  const service = servicePagesContent.find((s) => s.slug === slug)
  const faqData = slug ? serviceFAQs[slug] : undefined
  const faqSchema = faqData
    ? {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqData.map((f) => ({
          '@type': 'Question',
          name: f.question,
          acceptedAnswer: { '@type': 'Answer', text: f.answer },
        })),
      }
    : null

  if (!service) {
    return (
      <div className="pt-32 pb-20 px-4 text-center min-h-screen flex flex-col items-center justify-center">
        <p className="text-slate-600 dark:text-slate-400 text-lg">Service not found.</p>
        <Link to="/services" className="mt-6 btn-primary">
          View all services
        </Link>
      </div>
    )
  }

  return (
    <>
      {faqSchema && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      )}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.35 }}
        className="min-h-screen"
      >
      {/* Hero Section */}
      <section className="relative pt-28 sm:pt-32 pb-16 sm:pb-24 px-4 sm:px-6 lg:px-8 overflow-hidden bg-slate-50/50 dark:bg-slate-900/30">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_0%,rgba(26,69,176,0.08),transparent)] dark:bg-[radial-gradient(ellipse_80%_50%_at_50%_0%,rgba(255,215,0,0.05),transparent)] pointer-events-none" />
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
          <div className="absolute top-10 left-10 w-64 h-64 bg-primary/10 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-80 h-80 bg-accent/10 rounded-full blur-3xl" />
        </div>

        <div className="container-narrow relative z-10">
          <Link
            to="/services"
            className="inline-flex items-center gap-2 text-slate-500 hover:text-primary dark:hover:text-accent mb-8 font-medium text-sm transition-colors"
          >
            <ArrowLeft size={16} strokeWidth={2} /> Back to services
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-primary dark:text-white mb-4 sm:mb-6 leading-tight">
              {service.title}
            </h1>
            <p className="text-xl sm:text-2xl text-accent font-semibold mb-6 sm:mb-8 max-w-3xl">
              {service.tagline}
            </p>
            <p className="text-slate-600 dark:text-slate-400 text-base sm:text-lg leading-relaxed max-w-3xl">
              {service.intro}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8">
        <div className="container-wide">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            
            {/* Main Content */}
            <div className="lg:col-span-7 xl:col-span-8 space-y-12 sm:space-y-16">
              {service.sections.map((section, i) => {
                const Icon = icons[i % icons.length]
                return (
                  <motion.div
                    key={section.heading}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.5 }}
                    className="relative pl-6 sm:pl-8 border-l-2 border-primary/20 dark:border-accent/20"
                  >
                    <div className="absolute -left-[17px] top-0 w-8 h-8 rounded-full bg-white dark:bg-navy-900 border-2 border-primary dark:border-accent flex items-center justify-center">
                      <Icon size={14} className="text-primary dark:text-accent" />
                    </div>
                    <h2 className="text-2xl sm:text-3xl font-bold text-slate-800 dark:text-slate-100 mb-4">{section.heading}</h2>
                    <p className="text-slate-600 dark:text-slate-400 leading-relaxed text-base sm:text-lg">{section.content}</p>
                  </motion.div>
                )
              })}
            </div>

            {/* Sidebar / Outcome Card */}
            <div className="lg:col-span-5 xl:col-span-4 lg:sticky lg:top-32">
              <motion.div
                className="card-glass p-6 sm:p-8"
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <div className="w-12 h-12 rounded-2xl bg-accent/10 dark:bg-accent/20 flex items-center justify-center mb-6">
                  <Target className="text-accent-text dark:text-accent" size={24} strokeWidth={2} />
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-slate-800 dark:text-slate-100 mb-6">
                  {service.outcomeTitle}
                </h3>
                <ul className="space-y-4">
                  {service.outcomeBullets.map((bullet, j) => (
                    <li key={j} className="flex items-start gap-3 text-slate-600 dark:text-slate-400 text-sm sm:text-base">
                      <CheckCircle2 className="text-primary dark:text-accent shrink-0 mt-0.5" size={20} />
                      <span>{bullet}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-8 pt-8 border-t border-slate-200 dark:border-slate-700/50">
                  <p className="text-slate-800 dark:text-slate-200 font-medium mb-4">
                    Ready to implement this system?
                  </p>
                  <Link to="/contact" className="btn-primary w-full justify-center" onClick={() => trackEvent('cta_click', { cta_name: 'book_strategy_call', from: 'service_page' })}>
                    Book Strategy Call
                  </Link>
                </div>
              </motion.div>
            </div>

          </div>
        </div>
      </section>

      {/* FAQ Section (AEO) */}
      {faqData && (
        <section className="py-16 px-4 bg-slate-50 dark:bg-navy-900/50">
          <div className="container-narrow">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-800 dark:text-white mb-8">
              Frequently Asked Questions
            </h2>
            <div className="space-y-6">
              {faqData.map((item, i) => (
                <div key={i} className="border-b border-slate-200/60 dark:border-white/10 pb-6">
                  <h3 className="font-semibold text-slate-800 dark:text-white mb-2">{item.question}</h3>
                  <p className="text-slate-600 dark:text-slate-300 leading-relaxed">{item.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Bottom CTA */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8 bg-primary/5 dark:bg-primary/10">
        <div className="container-narrow text-center">
          <motion.p
            className="text-slate-600 dark:text-slate-400 italic text-base sm:text-lg mb-8 max-w-2xl mx-auto"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            "All services at DigiSutra follow one core principle: Simple systems create sustainable growth."
          </motion.p>
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}>
            <Link to="/services" className="btn-secondary">
              Explore Other Services
            </Link>
          </motion.div>
        </div>
      </section>
      </motion.div>
    </>
  )
}
